import { t } from "@travily/i18n";
import type ProgramsIndex from "@travily/types/pages/ProgramsIndex";
import type ProgramSearchHit from "@travily/types/pending/models/ProgramSearchHit";
import { Icon } from "@ui/components/Icon";
import type { WhereSelection } from "@ui/components/SearchBar";
import { useCallback, useMemo } from "react";
import { ProgramCard } from "@/components/ProgramCard/ProgramCard";
import { ProgramCardSkeleton } from "@/components/ProgramCard/ProgramCardSkeleton";
import { type SearchMapPinsOptions, SearchMapSection } from "@/components/Search/SearchMapSection";
import { SearchResultsBody } from "@/components/Search/SearchResultsBody";
import { SearchResultsHeading } from "@/components/Search/SearchResultsHeading";
import { useSearchResults } from "@/hooks/use-search-results";
import type { SearchWindow } from "@/lib/when-search-window";
import type { Bbox } from "@/lib/where-bounding-boxes";
import type { MapCluster } from "@/types/search-map";
import { ProgramMapPinCard } from "./components/ProgramMapPinCard";
import { hitToProgram } from "./hit-to-program";

// `_source` fields pulled for single-listing map pins — enough for the pin popup card. Hoisted so
// the reference stays stable across renders (it's a `useSearchMapPins` effect dependency).
const PROGRAM_PIN_SOURCE_FIELDS = ["id", "sqid", "name", "tagline", "cover_image", "photos", "account", "locations"];

const getProgramHitId = (hit: ProgramSearchHit) => hit.sqid;

// Single-listing pins render as a name pill (consistent with destinations).
const getProgramPinLabel = (cluster: MapCluster<ProgramSearchHit>) => cluster.hit?.name ?? null;

interface ProgramsSearchAdapterProps {
  getFavorited?: (type: string, id: string | number) => boolean;
  onFavoriteToggle?: (type: string, id: string | number) => void;
  /** Server-computed default first page (see Web::Concerns::SeedResults). */
  seedPrograms?: ProgramsIndex["seed_results"];
  /** The default search's total, seeded so the H1 count is server-rendered. */
  seedTotal?: ProgramsIndex["seed_total"];
  whereValue: WhereSelection | null;
  whereBoundsBox: string | null;
  whereBbox: Bbox | null;
  whenWindow: SearchWindow | null;
  whoAges: number[] | null;
  markMapArea: (label: string) => void;
}

export function ProgramsSearchAdapter({
  getFavorited,
  onFavoriteToggle,
  seedPrograms,
  seedTotal,
  whereValue,
  whereBoundsBox,
  whereBbox,
  whenWindow,
  whoAges,
  markMapArea,
}: ProgramsSearchAdapterProps) {
  const results = useSearchResults<ProgramSearchHit>({ sessionKey: "programs" });

  // The When window rides the request body via `useProgramsSearch`'s
  // requestEnhancer (the backend builds the date filter from it), so it isn't part
  // of InstantSearch's query state. Mirror it into a Configure `ruleContexts` value
  // — Searchkit ignores the param, but InstantSearch treats a change as a new
  // search, which both re-runs the query (re-invoking the enhancer) and busts the
  // infinite-hits cache so stale hits don't linger when only the count changed.
  const whenContexts = useMemo(
    () => (whenWindow ? [`when:${whenWindow.gte ?? ""}:${whenWindow.lte ?? ""}`] : undefined),
    [whenWindow],
  );
  // Same trick for "Who": the ages ride the request body (the backend builds the
  // filter), so mirror them into a ruleContext to bust the cache and re-run the
  // query when the selection changes. Sorted so member order never matters.
  const whoContexts = useMemo(
    () => (whoAges && whoAges.length > 0 ? [`who:${[...whoAges].sort((a, b) => a - b).join("-")}`] : undefined),
    [whoAges],
  );
  const ruleContexts = useMemo(() => {
    const contexts = [...(whenContexts ?? []), ...(whoContexts ?? [])];
    return contexts.length > 0 ? contexts : undefined;
  }, [whenContexts, whoContexts]);

  // The When window + Who ages ride the map-pins request body too (the programs search front door
  // reads them), so map clusters and result cards apply the same date + family filters.
  // Keys match WHEN_PAYLOAD_KEY / WHO_PAYLOAD_KEY.
  const pins = useMemo<SearchMapPinsOptions>(() => {
    const extra: Record<string, unknown> = {};
    if (whenWindow) extra._travily_when = whenWindow;
    if (whoAges && whoAges.length > 0) extra._travily_who = { ages: whoAges };
    return {
      host: "/api/programs",
      index: "programs",
      geoField: "locations",
      sourceFields: PROGRAM_PIN_SOURCE_FIELDS,
      extraBody: Object.keys(extra).length > 0 ? extra : undefined,
    };
  }, [whenWindow, whoAges]);

  const renderPin = useCallback(
    (hit: ProgramSearchHit, close: () => void, fullWidth = false) => (
      <ProgramMapPinCard
        favorited={getFavorited?.("Program", hit.sqid) ?? false}
        fullWidth={fullWidth}
        hit={hit}
        onClose={close}
        onFavoriteToggle={onFavoriteToggle ? () => onFavoriteToggle("Program", hit.sqid) : undefined}
      />
    ),
    [getFavorited, onFavoriteToggle],
  );

  const showLive = !(results.isInitialLoading && results.hits.length === 0);
  const count = showLive ? results.totalCount : (seedTotal ?? null);
  const heading = count === null ? null : t("components.views.programs.search.heading", { count });
  const seedCards = seedPrograms?.map((program) => (
    <ProgramCard
      favorited={getFavorited?.("Program", program.id) ?? false}
      key={program.id}
      onFavoriteToggle={onFavoriteToggle ? () => onFavoriteToggle("Program", program.id) : undefined}
      program={program}
      showAuthor
      theme="compact"
    />
  ));

  return (
    <SearchMapSection
      getHitId={getProgramHitId}
      heading={<SearchResultsHeading heading={heading} />}
      headingLabel={heading ?? t("components.frames.app_layout.nav.programs")}
      sheetHeading={<SearchResultsHeading heading={heading} variant="sheet" />}
      mapAreaLabel={t("components.views.programs.search.map_area")}
      mapEmptyLabel={t("components.views.programs.search.empty_title")}
      markMapArea={markMapArea}
      pinIcon={<Icon name="location" size="32" type="sticker" />}
      pinLabel={getProgramPinLabel}
      pins={pins}
      renderPin={renderPin}
      ruleContexts={ruleContexts}
      whereBbox={whereBbox}
      whereBoundsBox={whereBoundsBox}
      whereValue={whereValue}
    >
      <SearchResultsBody
        emptyDescription={t("components.views.programs.search.empty_description")}
        emptyTitle={t("components.views.programs.search.empty_title")}
        getKey={(hit) => hit.sqid}
        illustration="trips"
        renderCard={(hit) => (
          <ProgramCard
            favorited={getFavorited?.("Program", hit.sqid) ?? false}
            onFavoriteToggle={onFavoriteToggle ? () => onFavoriteToggle("Program", hit.sqid) : undefined}
            program={hitToProgram(hit)}
            showAuthor
            theme="compact"
          />
        )}
        renderSkeletonCard={(key) => <ProgramCardSkeleton key={key} />}
        results={results}
        seedCards={seedCards}
        skeletonCount={6}
      />
    </SearchMapSection>
  );
}
